/** Onboarding Pipeline (Demo Mode only) - standard list view of customers on the onboarding
 * journey (Prospect -> Contract Won -> Mobilising -> Onboarding -> Go-Live Ready) for the
 * Customer Management workspace. Follows the standard list-screen pattern (dashboard tiles,
 * quick filters, advanced filters, table + pagination) rather than a Kanban board, so it reads
 * consistently with the rest of the app (see RecipeV2 `/store/recipes-v2`). Tiles surface
 * cross-cutting portfolio signals (risk, upcoming go-lives, average readiness) rather than
 * repeating the per-stage counts already visible in the table. All figures are static mock
 * data, matching the "seeded illustrative numbers" approach used by `src/pages/EstateManagement`. */

import { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Accordion, AccordionDetails, AccordionSummary, Box, Button, Chip, Grid, IconButton,
  InputAdornment, LinearProgress, MenuItem, Stack, SvgIcon, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, Typography, useTheme,
} from '@mui/material';
import {
  RocketLaunchOutlined, ReportProblemOutlined, EventAvailable, TrendingUp, Clear, Search,
  ArrowForward, ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import PageArea from '../../../components/shared/PageArea';
import PageTitle from '../../../components/shared/PageTitle';
import { Section } from '../../../components/ui/Section';
import { TableHeader } from '../../../components/forms/TableHeader';
import { Pill } from '../../../components/shared/Pill';
import CustomPagination from '../../../components/forms/Pagination';
import {
  getMilestoneProgress, getOnboardingCustomers, JOURNEY_STAGE_COLOR, JOURNEY_STAGE_LABEL,
  JourneyStage, RISK_COLOR, RiskLevel,
} from '../../../mocks/data/customerManagementData';

const SEARCH_DEBOUNCE_MS = 300;
const MIN_SEARCH_LENGTH = 3;

const PIPELINE_STAGES: JourneyStage[] = ['Prospect', 'ContractWon', 'Mobilising', 'Onboarding', 'GoLiveReady'];

const QuickFilterDivider = () => (
  <div style={{ width: 1, height: 24, backgroundColor: '#E0E0E0', margin: '0 8px' }} />
);

const OnboardingPipeline = () => {
  const { t } = useTranslation('common');
  const theme = useTheme();
  const navigate = useNavigate();

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [stageFilter, setStageFilter] = useState<JourneyStage | ''>('');
  const [riskFilter, setRiskFilter] = useState<RiskLevel | ''>('');
  const [isAdvancedOpen, setIsAdvancedOpen] = useState(false);
  const [page, setPage] = useState(0);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const customers = getOnboardingCustomers();

  const handleSearchChange = (value: string) => {
    setSearch(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      if (value.length >= MIN_SEARCH_LENGTH || value.length === 0) {
        setDebouncedSearch(value);
        setPage(0);
      }
    }, SEARCH_DEBOUNCE_MS);
  };

  const handleClearFilters = () => {
    setSearch('');
    setDebouncedSearch('');
    setStageFilter('');
    setRiskFilter('');
    setPage(0);
  };

  const readinessById = useMemo(() => {
    const map = new Map<string, number>();
    customers.forEach((customer) => map.set(customer.id, getMilestoneProgress(customer).overallPercent));
    return map;
  }, [customers]);

  const filtered = useMemo(() => {
    const query = debouncedSearch.trim().toLowerCase();
    return customers.filter((customer) => {
      const matchesQuery = !query || customer.name.toLowerCase().includes(query) || customer.sector.toLowerCase().includes(query);
      const matchesStage = !stageFilter || customer.journeyStage === stageFilter;
      const matchesRisk = !riskFilter || customer.riskLevel === riskFilter;
      return matchesQuery && matchesStage && matchesRisk;
    });
  }, [customers, debouncedSearch, stageFilter, riskFilter]);

  const atRiskCount = customers.filter((customer) => customer.blockers.length > 0).length;
  const dueWithin30Days = customers.filter((customer) => {
    if (!customer.targetGoLive) return false;
    const days = (new Date(customer.targetGoLive).getTime() - Date.now()) / (1000 * 60 * 60 * 24);
    return days >= 0 && days <= 30;
  }).length;
  const averageReadiness = customers.length === 0 ? 0 : Math.round(
    customers.reduce((total, customer) => total + (readinessById.get(customer.id) ?? 0), 0) / customers.length,
  );

  const METRIC_TILES = [
    { label: t('onboardingPipelineTotal', 'Clients in pipeline'), value: `${customers.length}`, subtitle: t('onboardingPipelineTotalSub', 'Prospect through go-live ready'), icon: <RocketLaunchOutlined />, color: '#1976d2' },
    { label: t('onboardingPipelineAtRisk', 'At risk / blocked'), value: `${atRiskCount}`, subtitle: t('onboardingPipelineAtRiskSub', 'Have an open blocker'), icon: <ReportProblemOutlined />, color: '#d32f2f' },
    { label: t('onboardingPipelineDueSoon', 'Due to go live in 30 days'), value: `${dueWithin30Days}`, subtitle: t('onboardingPipelineDueSoonSub', 'Target go-live approaching'), icon: <EventAvailable />, color: '#ed6c02' },
    { label: t('onboardingPipelineAvgReadiness', 'Avg. go-live readiness'), value: `${averageReadiness}%`, subtitle: t('onboardingPipelineAvgReadinessSub', 'Across the pipeline'), icon: <TrendingUp />, color: '#2e7d32' },
  ];

  return (
    <PageArea>
      <PageTitle subTitle={t('onboardingPipelineSubTitle', 'Track every client from prospect to go-live ready, and jump into their onboarding detail to close out missing milestones.')}>
        {t('onboardingPipelineTitle', 'Onboarding Pipeline')}
      </PageTitle>

      <Grid container
        spacing={4}
        sx={{ mb: 6 }}>
        {METRIC_TILES.map((tile) => (
          <Grid item
            xs={12}
            sm={6}
            md={3}
            key={tile.label}>
            <Section style={{ p: 4, mb: 0 }}>
              <Box display="flex"
                alignItems="center"
                justifyContent="space-between">
                <Box flex={1}>
                  <Box sx={{ color: theme.palette.custom.gray[600], fontSize: '10px', textTransform: 'uppercase', mb: 0.25, fontWeight: 500 }}>
                    {tile.label}
                  </Box>
                  <Box sx={{ fontSize: '20px', fontWeight: 600, color: theme.palette.custom.gray[900], mb: 0.25 }}>
                    {tile.value}
                  </Box>
                  <Box sx={{ fontSize: '12px', color: theme.palette.custom.gray[600] }}>
                    {tile.subtitle}
                  </Box>
                </Box>
                <Box sx={{ backgroundColor: `${tile.color}20`, borderRadius: '50%', width: 48, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center', color: tile.color }}>
                  <SvgIcon fontSize="medium">{tile.icon}</SvgIcon>
                </Box>
              </Box>
            </Section>
          </Grid>
        ))}
      </Grid>

      <Grid container
        spacing={2}
        alignItems="center"
        sx={{ mb: 2 }}>
        <Grid item
          sx={{ display: 'flex', alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder={t('onboardingPipelineSearchPlaceholder', 'Search clients by name or sector')}
            value={search}
            onChange={(e) => handleSearchChange(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: search ? (
                <InputAdornment position="end">
                  <IconButton size="small"
                    onClick={() => handleSearchChange('')}>
                    <Clear fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : undefined,
            }}
            sx={{ minWidth: 260, mb: 0 }}
          />
        </Grid>

        <Grid item>
          <QuickFilterDivider />
        </Grid>

        <Grid item>
          <Grid container
            spacing={1}
            alignItems="center">
            {PIPELINE_STAGES.map((stage) => (
              <Grid item
                key={stage}>
                <Chip
                  label={t(`journeyStage${stage}`, JOURNEY_STAGE_LABEL[stage])}
                  color={stageFilter === stage ? 'primary' : 'default'}
                  onClick={() => { setStageFilter((prev) => (prev === stage ? '' : stage)); setPage(0); }}
                />
              </Grid>
            ))}
          </Grid>
        </Grid>
      </Grid>

      <Accordion
        variant="borderless"
        expanded={isAdvancedOpen}
        onChange={(_, expanded) => setIsAdvancedOpen(expanded)}
      >
        <AccordionSummary>
          <Grid container
            alignItems="center"
            justifyContent="space-between"
            sx={{ width: '100%' }}>
            <Grid item>
              <Grid container
                spacing={1}
                alignItems="center">
                <Grid item>
                  {t('onboardingPipelineAdvancedFilters', 'Advanced Filters')}
                </Grid>
                <Grid item
                  style={{ marginTop: '8px' }}>
                  {isAdvancedOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </Grid>
              </Grid>
            </Grid>
            <Grid item>
              <Button
                size="md"
                variant="secondary"
                onClick={handleClearFilters}
              >
                {t('onboardingPipelineClearFilters', 'Clear Filters')}
              </Button>
            </Grid>
          </Grid>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container
            spacing={3}>
            <Grid item
              lg={3}
              sm={6}
              xs={12}>
              <TextField
                select
                label={t('onboardingPipelineStageLabel', 'Journey Stage')}
                value={stageFilter}
                onChange={(e) => { setStageFilter(e.target.value as JourneyStage | ''); setPage(0); }}
                fullWidth
                size="small"
              >
                <MenuItem value="">{t('onboardingPipelineAllStages', 'All stages')}</MenuItem>
                {PIPELINE_STAGES.map((stage) => (
                  <MenuItem key={stage}
                    value={stage}>{t(`journeyStage${stage}`, JOURNEY_STAGE_LABEL[stage])}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item
              lg={3}
              sm={6}
              xs={12}>
              <TextField
                select
                label={t('onboardingPipelineRiskLabel', 'Risk Level')}
                value={riskFilter}
                onChange={(e) => { setRiskFilter(e.target.value as RiskLevel | ''); setPage(0); }}
                fullWidth
                size="small"
              >
                <MenuItem value="">{t('onboardingPipelineAllRisk', 'All risk levels')}</MenuItem>
                <MenuItem value="Low">{t('riskLow', 'Low')}</MenuItem>
                <MenuItem value="Medium">{t('riskMedium', 'Medium')}</MenuItem>
                <MenuItem value="High">{t('riskHigh', 'High')}</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      <Section style={{ marginTop: '16px' }}>
        <TableHeader>
          {t('onboardingPipelineListTitle', 'Onboarding clients')}
          <Pill text={`${filtered.length} ${t('onboardingPipelineCountLabel', 'clients')}`} />
        </TableHeader>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('onboardingPipelineCustomer', 'Client')}</TableCell>
                <TableCell>{t('onboardingPipelineStage', 'Journey Stage')}</TableCell>
                <TableCell>{t('onboardingPipelineReadiness', 'Go-Live Readiness')}</TableCell>
                <TableCell>{t('onboardingPipelineRisk', 'Risk')}</TableCell>
                <TableCell>{t('onboardingPipelineTargetGoLive', 'Target Go-Live')}</TableCell>
                <TableCell>{t('onboardingPipelineNextBestAction', 'Next Best Action')}</TableCell>
                <TableCell>{t('onboardingPipelineActions', 'Actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((customer) => {
                const readiness = readinessById.get(customer.id) ?? 0;
                return (
                  <TableRow key={customer.id}
                    hover>
                    <TableCell>
                      <Typography variant="textSM"
                        fontWeight={600}>{customer.name}</Typography>
                      <Typography variant="textXS"
                        color="text.secondary">{customer.sector}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={t(`journeyStage${customer.journeyStage}`, JOURNEY_STAGE_LABEL[customer.journeyStage])}
                        size="small"
                        color={JOURNEY_STAGE_COLOR[customer.journeyStage]}
                        variant="outlined" />
                    </TableCell>
                    <TableCell sx={{ minWidth: 160 }}>
                      <Stack spacing={0.5}>
                        <LinearProgress variant="determinate"
                          value={readiness}
                          sx={{ height: 6, borderRadius: 3 }} />
                        <Typography variant="textXS"
                          color="text.secondary">{readiness}%</Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Chip label={customer.riskLevel}
                        size="small"
                        color={RISK_COLOR[customer.riskLevel]} />
                    </TableCell>
                    <TableCell>{customer.targetGoLive || t('onboardingPipelineNotSet', 'Not set')}</TableCell>
                    <TableCell>
                      <Typography variant="textXS">{customer.nextBestAction}</Typography>
                    </TableCell>
                    <TableCell>
                      <IconButton size="small"
                        title={t('onboardingPipelineOpenOnboarding', 'Open onboarding detail')}
                        onClick={() => navigate(`/customer-management/onboarding/${customer.id}`)}>
                        <ArrowForward fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7}>
                    <Typography variant="textSM"
                      color="text.secondary">{t('onboardingPipelineNoResults', 'No clients match your filters.')}</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <CustomPagination page={page}
          setPage={setPage}
          maxPages={1}
          useNoDataMessage={false}
          breakpointForChangeDisplay={120000} />
      </Section>
    </PageArea>
  );
};

export default OnboardingPipeline;
